import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Router } from '@angular/router';

import { lastValueFrom } from 'rxjs/internal/lastValueFrom';
import { Observable } from 'rxjs/internal/Observable';

import { InteractiveDocumentInstance } from '../models/interactiveDocumentInstance';
import { User } from '@app/models';
import { InteractiveDocumentInstanceActionsService } from './interactive-document-instance-actions.service';
import { SharedUtilsService } from './shared-utils.service';
import { CredentialingChecklistItem } from '@app/modules/credentialing/credentialing-setups/credentialing-setups.service';
import { DocumentService } from './document.service';
import { UserService } from './user.service';
import { GenericModuleService } from './generic-module.service';
import { EmailAddressService } from './email-address.service';

export interface InteractiveDocumentInstanceApi {
  items: InteractiveDocumentInstance[];
  total_count: number;
}

export interface BulkRecipient {
  name: string;
  email: string;
  user?: any;
}

@Injectable({ providedIn: 'root' })
export class InteractiveDocumentInstanceService {
  private currentDocument;

  private API_URL = '/api/interactive-document-instances/';

  private headerOptions = { headers: new HttpHeaders({ 'Content-Type': 'application/json' }) };

  private bulkRecipients: BulkRecipient[] = []; // Used for bulk agreement send.

  constructor(
    private httpClient: HttpClient,
    private _interactiveDocumentActionService: InteractiveDocumentInstanceActionsService,
    private _documentService: DocumentService,
    private _sharedUtilsService: SharedUtilsService,
    private _userService: UserService,
    private _genericModuleService: GenericModuleService,
    private _emailAddressService: EmailAddressService,
    private _router: Router
  ) { }


  setCurrent(_document): void {
    this.currentDocument = _document;
  }


  getCurrent() {
    return this.currentDocument;
  }


  setBulkRecipients(_bulkRecipients: BulkRecipient[] = []): void {
    this.bulkRecipients = _bulkRecipients?.length ? [..._bulkRecipients] : [];
  }


  getBulkRecipients() {
    return this.bulkRecipients;
  }


  // Orchestrator for the "select records in a grid -> start a bulk agreement send" flow. Called from selection-action-menu and any grid that has its own inline Bulk Agreement Send button.
  // Builds the recipient list from the selected records, validates it, stores it for the send page to consume, then navigates to /document-center/send with the bs=true trigger param. Shows a friendly error and stays put if no record yielded a valid email. If some records were dropped, surfaces a notification so the user knows the count does not match what they selected.
  triggerBulkAgreementSendFromRecords(_records: any[], _module: any): void {
    const _totalSelected = _records?.length ?? 0;
    const _bulkRecipients = this.buildBulkRecipientsFromRecords(_records, _module);

    if (!_bulkRecipients.length) {
      this._sharedUtilsService.showErrorDialog('No valid recipients could be built for the selected records. Please select different records and try again.');
      return;
    }

    const _droppedCount = _totalSelected - _bulkRecipients.length;
    if (_droppedCount > 0) this._sharedUtilsService.showMintNotificationWithOptions(_bulkRecipients.length + ' of ' + _totalSelected + ' selected added. ' + _droppedCount + ' skipped (missing email or duplicate).', 10000, 'success'); // Held longer so the user actually reads it - default 1s is too quick for a multi-part message. Click-to-dismiss is built into the snackbar component.

    this.setBulkRecipients(_bulkRecipients);
    this._router.navigate([`/app/document-center/send`], { queryParams: { bs: true } });
  }


  // Extract a deduped BulkRecipient[] from a set of selected grid records. Pulls names through the central GenericModuleService.getNameForModule so naming stays consistent with how the rest of the app labels these records.
  // Accepts either a full SsModule object OR just the module name string (for hardcoded grids that do not load a full module record).
  buildBulkRecipientsFromRecords(_records: any[], _module: any): BulkRecipient[] {
    const _moduleForLookup: any = (typeof _module === 'string') ? { name: _module } : _module; // Normalize - getNameForModule only special-cases the literal string 'users' and expects an object (with .name) for everything else.
    const _recipients: BulkRecipient[] = [];

    for (const _r of (_records ?? [])) {
      const _email = this.getEmailFromRecord(_r);
      if (!_email?.length) continue; // Skip records that yield no usable email - they cannot receive an agreement. Caller surfaces the dropped count via notification.
      if (_recipients.some(_rec => _rec?.email === _email)) continue; // Dedup by email so the same person does not get N copies.

      _recipients.push({
        name: this._genericModuleService.getNameForModule(_moduleForLookup, _r) ?? '',
        email: _email,
        user: this.getRecipientUserFromRecord(_r, _moduleForLookup)
      });
    }

    return _recipients;
  }


  // Three-tier email lookup. Prefers the direct .email field, then the standard email_addresses array (via EmailAddressService), then the legacy record_emails array as a final fallback.
  private getEmailFromRecord(_record: any): string {
    if (!_record) return '';

    let _email = (_record?.email || '').trim(); // Tier 1 - direct field.

    if (!_email.length && this._emailAddressService?.getEmailFromRecord) {
      const _emailObj: any = this._emailAddressService.getEmailFromRecord(_record); // Tier 2 - standard email_addresses array. Returns the full object so pull the address off.
      _email = (_emailObj?.email_address || _emailObj || '').trim();
    }

    if (!_email.length && Array.isArray(_record?.record_emails)) {
      const _primary = _record.record_emails.find((_e: any) => _e && _e?.primary); // Tier 3 - legacy record_emails array. Prefer primary, then any non-invalid + non-opted-out.
      const _fallback = _primary || _record.record_emails.find((_e: any) => _e && !_e?.invalid_email && !_e?.opt_out);
      _email = (_fallback?.email_address || '').trim();
    }

    return _email;
  }


  // Resolve the User object to attach to the BulkRecipient (so downstream code can show user-related UI). For the users module the record IS the user; otherwise look for a related_user link.
  private getRecipientUserFromRecord(_record: any, _module: any): any {
    if (!_record) return null;
    const _moduleName = (typeof _module === 'string') ? _module : (_module?.name ?? null);

    if (_moduleName === 'users') return _record; // The record itself is the user record.
    if (_record?.related_user) return _record.related_user;
    return null;
  }


  getNewInstance(_currentUser: User): Promise<InteractiveDocumentInstance> {
    return new Promise(async (resolve, reject) => {
      const _newInstance = new InteractiveDocumentInstance();
      _newInstance.interactive_document = null;
      _newInstance.created_by = _currentUser._id;
      _newInstance.deleted = false;
      _newInstance.completeInOrder = true;
      _newInstance.status = 'Created';

      resolve(_newInstance);
    });
  }


  doesConditionalInputPass(_input, _documentInstance, _allInputs: any[] = []): Promise<boolean> {
    return new Promise(async (resolve) => {
      try {
        // console.log('Conditional _input: ', _input);
        const conditionsControl = _input.conditionsControl;
        // console.log('conditionsControl: ', conditionsControl);

        const whenOption = conditionsControl.whenOption;
        const conditionAction = conditionsControl.conditionActionResult;

        // console.log('When option: ', whenOption);
        // console.log('conditionAction: ', conditionAction);

        let inputCriteria = (conditionsControl && conditionsControl.criteria?.length) ? conditionsControl.criteria : [];

        if (inputCriteria && inputCriteria.length) {
          inputCriteria = inputCriteria.filter(_c => _c.relatedField && _c.relatedField.systemFieldName); // filter out that empty placeholder
        }

        // console.log('inputCriteria: ', inputCriteria);

        let passesChecks: boolean = false;
        const mathOperators: string[] = ['Less Than', 'Less Than or Equal To', 'Greater Than', 'Greater Than or Equal To'];
        const inclusionOperators: string[] = ['In', 'Not In', 'Contains', 'Does Not Contain'];

        const criteriaValues = inputCriteria.map(_c => {
          return { systemFieldName: _c.relatedField.systemFieldName, value: _documentInstance.values[_c.relatedField.systemFieldName] }
        });

        // console.log('Criteria values: ', criteriaValues);

        for (let _criteriaIndex = 0; _criteriaIndex < inputCriteria.length; _criteriaIndex++) {
          // console.log('Criteria ' + (_criteriaIndex + 1));
          // console.log('');

          const criteriaToCheck = inputCriteria[_criteriaIndex];
          const criteriaOperator = criteriaToCheck.comparisonOperator;
          const conditionalFieldValue = criteriaToCheck?.comparisonValue ?? null;
          let relatedFieldValue = _documentInstance.values[criteriaToCheck.relatedField.systemFieldName];

          // console.log('criteriaToCheck: ', criteriaToCheck);
          // console.log('criteriaOperator: ', criteriaOperator);
          // console.log('conditionalFieldValue: ', conditionalFieldValue);
          // console.log('relatedFieldValue: ', relatedFieldValue);

          // Radio/Checkbox fields are sometimes null instead of false. If null and looking for false try to make corrections here.
          if (relatedFieldValue == undefined && typeof conditionalFieldValue === 'boolean') {
            if (!_allInputs?.length) _allInputs = this.getAllInputsFromDocInstance(_documentInstance) ?? []; // If not passed build it here.
            const relatedField = _allInputs.find(_i => _i?.field?.systemFieldName === criteriaToCheck?.relatedField?.systemFieldName); // Find full relate field.
            // console.log('relatedField: ', relatedField);

            if (relatedField?.type?.inputType && ['Checkbox', 'Radio'].includes(relatedField.type.inputType)) relatedFieldValue = false;// If one of these conditions set false.
          }

          let breakEarly: boolean = false; // If want to break the loop early for some reason. Like we check for all at once in empty/not empty.

          if (whenOption === 'Any') {
            // use OR - if ONE is TRUE, Return TRUE
            if (criteriaOperator === 'Not Empty') {
              // if any are not null or false
              const valuesPassingCheck = (criteriaValues?.length) ? criteriaValues.filter(_v => ['', null, 'false', false].includes(_v.value)) : [];
              // console.log('valuesPassingCheck: ', valuesPassingCheck);
              passesChecks = (valuesPassingCheck.length) ? false : true;
              breakEarly = true;
            } else if (criteriaOperator === 'Empty') {
              // if any are not null or false
              const criteriaWithEmpty = (criteriaValues?.length) ? criteriaValues.filter(_v => ['', null, 'false', false].includes(_v.value)) : [];
              // console.log('criteriaWithEmpty: ', criteriaWithEmpty);

              // if any are empty or null, then it passes checks
              passesChecks = (criteriaWithEmpty.length) ? true : false;
              breakEarly = true;
            } else if (['Equals', 'Not Equals'].includes(criteriaOperator)) {
              // Don't show if null or empty value. Allow false through for checkbox and radio checks.
              if ((relatedFieldValue != undefined && relatedFieldValue !== '') || relatedFieldValue === false) {
                // Use helper function to see if these two values match checking types.
                const isMatch = this.valuesMatch(relatedFieldValue, conditionalFieldValue);

                if (criteriaOperator === 'Equals') passesChecks = isMatch;
                else passesChecks = !isMatch ? true : false; // If matches it is false.
              }
            } else if (mathOperators.includes(criteriaOperator)) {
              // Don't show if no or empty value.
              if (relatedFieldValue != undefined && relatedFieldValue !== '') {
                const isDate: boolean = relatedFieldValue instanceof Date && !isNaN(relatedFieldValue.getTime()); // Do we need to work with a date.

                // If not a date treat as number. Otherwise we must do the date logic using get time.
                if (!isDate) passesChecks = this.meetsMathCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
                else passesChecks = this.meetsDateCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
              }
            } else if (inclusionOperators.includes(criteriaOperator)) {
              // Don't show if no or empty value.
              if (relatedFieldValue != undefined && relatedFieldValue !== '') {
                passesChecks = this.meetsInclusionCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
              }
            }

            if (breakEarly || passesChecks === true) break;
          } else if (whenOption === 'All') {
            // use AND - if ONE is false, RETURN false
            if (criteriaOperator === 'Not Empty') {
              const valuesPassingCheck = (criteriaValues?.length) ? criteriaValues.filter(_v => ['', null, 'false', false].includes(_v.value)) : [];
              // console.log('valuesPassingCheck: ', valuesPassingCheck);
              passesChecks = (valuesPassingCheck.length === inputCriteria.length) ? true : false; // all values passing check must match the number of criteria
            } else if (criteriaOperator === 'Empty') {
              // if any are not null or false
              const criteriaWithEmpty = (criteriaValues?.length) ? criteriaValues.filter(_v => ['', null, 'false', false].includes(_v.value)) : [];
              // console.log('criteriaWithEmpty: ', criteriaWithEmpty);

              // if any are empty or null, then it passes checks
              passesChecks = (criteriaWithEmpty.length === inputCriteria.length) ? true : false; // all values passing check must match the number of criteria
            } else if (['Equals', 'Not Equals'].includes(criteriaOperator)) {
              // Don't show if null or empty value. Allow false through for checkbox and radio checks.
              if ((relatedFieldValue != undefined && relatedFieldValue !== '') || relatedFieldValue === false) {
                // Use helper function to see if these two values match checking types.
                const isMatch = this.valuesMatch(relatedFieldValue, conditionalFieldValue);

                if (criteriaOperator === 'Equals') passesChecks = isMatch;
                else passesChecks = !isMatch ? true : false; // If matches it is false.
              }
            } else if (mathOperators.includes(criteriaOperator)) {
              // Don't show if no or empty value.
              if (relatedFieldValue != undefined && relatedFieldValue !== '') {
                const isDate: boolean = relatedFieldValue instanceof Date && !isNaN(relatedFieldValue.getTime()); // Do we need to work with a date.

                // If not a date treat as number. Otherwise we must do the date logic using get time.
                if (!isDate) passesChecks = this.meetsMathCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
                else passesChecks = this.meetsDateCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
              }
            } else if (inclusionOperators.includes(criteriaOperator)) {
              // Don't show if no or empty value.
              if (relatedFieldValue != undefined && relatedFieldValue !== '') {
                passesChecks = this.meetsInclusionCriteria(criteriaOperator, relatedFieldValue, conditionalFieldValue);
              }
            }

            if (breakEarly || passesChecks === false) break;
          }
        }

        // console.log('passesChecks: ', passesChecks);

        if (conditionAction === 'Disable') {
          _input.field.readOnly = passesChecks;
        } else if (conditionAction === 'Enable') {
          _input.field.readOnly = !passesChecks;
        }

        _input.field.conditionAction = conditionAction;
        _input.field.passesCriteria = passesChecks;

        // console.log('Field after checking disabled: ', _input);

        resolve(passesChecks);
      } catch (_err) {
        // Catch any other errors that may occur during the process
        console.log('Error doesConditionalInputPass:', _err);
        resolve(true);
      }
    });
  }


  valuesMatch(_inputValue: any, _conditionValue: any): boolean {
    if (_conditionValue == undefined) return true; // If no value was set for the condition just show it.

    if (typeof _inputValue === 'string') {
      return _inputValue?.toLowerCase() === String(_conditionValue)?.toLowerCase();
    }

    if (typeof _inputValue === 'number' || !isNaN(Number(_inputValue))) {
      const relatedNum = Number(_inputValue);
      const conditionalNum = Number(_conditionValue);
      return !isNaN(relatedNum) && !isNaN(conditionalNum) && relatedNum === conditionalNum;
    }

    if (_inputValue instanceof Date || !isNaN(Date.parse(_inputValue))) {
      const relatedDate = new Date(_inputValue);
      const conditionalDate = new Date(_conditionValue);
      return !isNaN(relatedDate?.getTime()) && !isNaN(conditionalDate?.getTime()) && relatedDate.getTime() === conditionalDate.getTime();
    }

    if (Array.isArray(_inputValue)) {
      if (Array.isArray(_conditionValue)) return this._sharedUtilsService.arraysAreEqual(_inputValue, _conditionValue);
      else return _inputValue.includes(_conditionValue);
    }

    return _inputValue === _conditionValue;
  }


  meetsInclusionCriteria(criteriaOperator: string, _inputValue: any, _conditionValue: any): boolean {
    if (_conditionValue == undefined || _conditionValue === '') return true; // If no condition was set, assume it passes.
    if (_inputValue == undefined || _inputValue === '') return false; // If the input is empty or undefined, it fails.

    // Helper to convert to string and format as lowercase.
    const lowercaseString = (val: any): string => String(val ?? '')?.toLowerCase();

    // Checking if they are arrays incase we have to support case for these.
    const inputIsArray = Array.isArray(_inputValue);
    const conditionIsArray = Array.isArray(_conditionValue);

    // If the _inputValue is in the _conditionValue.
    if (['In', 'Contains'].includes(criteriaOperator)) {
      // If both are arrays, every value in _inputValue must exist in _conditionValue.
      if (inputIsArray && conditionIsArray) {
        const mappedConditions = _conditionValue.map(_v => lowercaseString(_v));
        return _inputValue.every(_val => mappedConditions.includes(lowercaseString(_val)));
      } else {
        const conditionValueFormatted = lowercaseString(_conditionValue);
        return conditionValueFormatted.includes(lowercaseString(_inputValue));
      }
    }

    // If the _inputValue is "Not" in the _conditionValue.
    if (['Not In', 'Does Not Contain'].includes(criteriaOperator)) {
      // If both are arrays, none of the values in _inputValue are present in _conditionValue.
      if (inputIsArray && conditionIsArray) {
        const mappedConditions = _conditionValue.map(_v => lowercaseString(_v));
        return _inputValue.every(_val => !mappedConditions.includes(lowercaseString(_val)));
      } else {
        const conditionValueFormatted = lowercaseString(_conditionValue);
        return !conditionValueFormatted.includes(lowercaseString(_inputValue));
      }
    }

    return false; // Fall back to false if operator isn't supported.
  }


  convertAgreementToDocOnProvider(_itemToCreateFrom: CredentialingChecklistItem, _relatedProviderId): Promise<CredentialingChecklistItem> {
    // DO SAME FOR DROPDOWN APPROVE
    return new Promise(async (resolve) => {
      console.log('Converting this agreement to a document attached to provider: ', _itemToCreateFrom);
      // console.log('PROVIDER ID: ', _relatedProviderId);

      const agreementToConvert = (_itemToCreateFrom?.completionDetails?.actionValue) ? _itemToCreateFrom.completionDetails.actionValue : null;
      // console.log('Agreement to convert: ', agreementToConvert);

      const documentLocationData = await lastValueFrom(this.downloadWithCoc(agreementToConvert));
      // console.log('documentLocationData: ', documentLocationData);

      if (documentLocationData) {
        const doc = {
          name: _itemToCreateFrom.label,
          mainModuleType: 'providers',
          relatedRecord: _relatedProviderId,
          document_type: _itemToCreateFrom.category,
          status: 'Active',
          filename: _itemToCreateFrom.label,
          providers_documents: _relatedProviderId,
          path: documentLocationData?.relativeUrl,
          expiration_date: null,
          created_by: agreementToConvert._id
        };

        const savedDoc = await lastValueFrom(this._documentService.create(doc));
        console.log('savedDoc: ', savedDoc);
      }

      resolve(null);
    });
  }  


  meetsMathCriteria(criteriaOperator: string, _inputValue: any, _conditionValue: any): boolean {
    if (_inputValue != undefined && _inputValue !== '') {
      const relatedNum = Number(_inputValue);
      const conditionalNum = _conditionValue != undefined ? Number(_conditionValue) : 0; // Default to zero if not set.
      // console.log('*** Doing Math comparison!!');

      if (!isNaN(relatedNum) && !isNaN(conditionalNum)) {
        switch (criteriaOperator) {
          case 'Less Than':
            return relatedNum < conditionalNum;
          case 'Less Than or Equal To':
            return relatedNum <= conditionalNum;
          case 'Greater Than':
            return relatedNum > conditionalNum;
          case 'Greater Than or Equal To':
            return relatedNum >= conditionalNum;
          default:
            return false;
        }
      }
    }
    return false; // Return false if the value is invalid or not set.
  }


  meetsDateCriteria(criteriaOperator: string, _inputValue: any, _conditionValue: any): boolean {
    if (_inputValue != undefined && _conditionValue != undefined) {
      const inputTime = new Date(_inputValue).getTime();
      const conditionTime = new Date(_conditionValue).getTime();
      // console.log('*** Doing Date comparison!!');

      if (!isNaN(inputTime) && !isNaN(conditionTime)) {
        switch (criteriaOperator) {
          case 'Less Than':
            return inputTime < conditionTime;
          case 'Less Than or Equal To':
            return inputTime <= conditionTime;
          case 'Greater Than':
            return inputTime > conditionTime;
          case 'Greater Than or Equal To':
            return inputTime >= conditionTime;
          default:
            return false;
        }
      }
    }
    return false; // Return false if either of the values are invalid.
  }


  getAllInputsFromDocInstance(_documentInstance) {
    let allInputs: any[] = [];

    if (_documentInstance?.doc_data?.pages?.length) {
      const unflattenedInputs = _documentInstance.doc_data.pages.map(_p => _p.inputs.map(_pi => {
        _pi.pageNo = _p.pageNo;

        return _pi;
      }));

      allInputs = this._sharedUtilsService.flatten(unflattenedInputs);
      // console.log('All inputs: ', allInputs);
    }

    return allInputs;
  }


  requestSignatureFromRecipient(_instance, _recipient, _userRequestingSig) {
    // console.log('_recipient: ', _recipient);

    let requestor: string = (_userRequestingSig) ? this._userService.getDisplayName(_userRequestingSig) : null;
    let requestorEmail: string = (_userRequestingSig?.email) ? _userRequestingSig.email : _instance?.created_by?.email;

    // If not signed in or manually set, _userRequestingSig could be undefined sometimes. so set defaults.
    if (!requestor?.trim()) {
      const creator = _instance?.created_by ?? null;
      if (creator) requestor = this._userService.getDisplayName(creator) || 'EDU Healthcare'; // If empty string returned, fallback to edu.
      else requestor = 'EDU Healthcare';
    }

    if (!requestor?.trim()) requestor = 'info@eduhealthcare.com';

    return new Promise(async (resolve) => {
      const sendResult: any = await lastValueFrom(this.requestSignature({
        recipientId: _recipient?.recipientId,
        docId: (_instance?._id) ? _instance._id : _instance,
        requestor: requestor,
        requestorEmail: requestorEmail,
        requestee: _recipient?.email,
        email: _recipient?.email,
        docName: _instance?.name,
        notes: _instance?.description
      }));

      // Only log the action when the email really went out. A failed send with a logged
      // 'signature requested' action would look sent forever and the reminder cron would
      // never retry it. Matches the pattern in the backend's bulkSendSignatures.
      if (sendResult?.sent) {
        const actionDescription = 'Sent out for signature to ' + _recipient?.email;
        await this._interactiveDocumentActionService.logAction('signature requested', _instance?._id, actionDescription, null, _recipient?.email);
      }

      resolve(sendResult);
    });
  }


  create(_document): Observable<InteractiveDocumentInstance> {
    return this.httpClient.post<any>(this.API_URL, JSON.stringify(_document), this.headerOptions);
  }


  // Save many pre-built instances in a single request. Returns the saved instances in the same order
  // they were sent, so callers can map results back to their source items by index.
  bulkCreate(_documents): Observable<InteractiveDocumentInstance[]> {
    return this.httpClient.post<InteractiveDocumentInstance[]>(this.API_URL + 'bulk-create', JSON.stringify({ instances: _documents }), this.headerOptions);
  }


  // Kick off backend signature sends for many already-saved instance ids. Server responds immediately and the actual sends run in the background, throttled.
  bulkSendSignatures(_instanceIds: string[]): Observable<{ accepted: boolean; queued: number; }> {
    return this.httpClient.post<{ accepted: boolean; queued: number; }>(this.API_URL + 'bulk-send-signatures', JSON.stringify({ instanceIds: _instanceIds }), this.headerOptions);
  }


  // Lightweight existence check — returns the subset of the given ids that still exist (not deleted).
  existingInstanceIds(_ids: string[]): Observable<string[]> {
    return this.httpClient.post<string[]>(this.API_URL + 'exists-by-ids', JSON.stringify({ ids: _ids }), this.headerOptions);
  }


  updateFieldsByIdArray(arrayOfIds, fieldsToUpdate): Observable<any[]> {
    const updateRequest = { ids: arrayOfIds, fields: fieldsToUpdate };
    return this.httpClient.put<any[]>(this.API_URL + 'update-certain-fields-array/', JSON.stringify(updateRequest), this.headerOptions);
  }


  updateFieldsById(_id, fieldsToUpdate): Observable<InteractiveDocumentInstance[]> {
    return this.httpClient.put<InteractiveDocumentInstance[]>(this.API_URL + 'update-certain-fields/' + _id, JSON.stringify(fieldsToUpdate), this.headerOptions);
  }


  update(_document): Observable<InteractiveDocumentInstance> {
    return this.httpClient.put<InteractiveDocumentInstance>(this.API_URL + _document._id, JSON.stringify(_document), this.headerOptions);
  }


  sendCopyToParticipants(_document, _typesToSend: string[], _signerMessage: string, _approverMessage: string, _sendToAll: boolean, _emailOverrides: string[] = null): Observable<any> {
    // Fall back to both types if not passed or empty _typesToSend array.
    const approvalCopyRequest = {
      document: { _id: _document._id, status: _document.status, name: _document.name, recipients: _document.recipients, interactive_document: _document.interactive_document }, // do not need entire doc here
      typesToSend: (_typesToSend?.length) ? _typesToSend : ['signer', 'approver'],
      signerMessage: _signerMessage ?? null,
      approverMessage: _approverMessage ?? null,
      sendToAll: _sendToAll,
      emailOverrides: _emailOverrides
    };

    return this.httpClient.post<any>(this.API_URL + 'send-copies', JSON.stringify(approvalCopyRequest), this.headerOptions);
  }


  sendCocPdfToParticipants(_document): Observable<any> {
    const convertCocRequest = { document: _document };
    return this.httpClient.post<any>(this.API_URL + 'send-doc-pdf', JSON.stringify(convertCocRequest), this.headerOptions);
  }


  convertToDocument(_document): Observable<InteractiveDocumentInstance> {
    return this.httpClient.post<InteractiveDocumentInstance>(this.API_URL + 'convert-to-document', JSON.stringify(_document), this.headerOptions);
  }


  downloadWithCoc(_document): Observable<any> {
    return this.httpClient.post<InteractiveDocumentInstance>(this.API_URL + 'download-with-coc', JSON.stringify(_document), this.headerOptions);
  } 


  downloadInstances(_document): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'download', JSON.stringify(_document), this.headerOptions);
  }


  search(searchTerms): Observable<InteractiveDocumentInstance[]> {
    searchTerms['selectAll'] = true;
    return this.httpClient.post<InteractiveDocumentInstance[]>(this.API_URL + 'dataSourceSearch', JSON.stringify(searchTerms), this.headerOptions);
  }


  requestSignature(sigReq): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'request-signature', JSON.stringify(sigReq), this.headerOptions);
  }


  resendSingleAgreementReminder(_instance): Observable<any> {
    return this.httpClient.post<any>(this.API_URL + 'resend-single-agreement-reminder', JSON.stringify({ agreement: _instance }), this.headerOptions);
  }


  searchAll(searchTerms): Observable<InteractiveDocumentInstance[]> {
    return this.httpClient.post<InteractiveDocumentInstance[]>(this.API_URL + 'search', JSON.stringify(searchTerms), this.headerOptions);
  }


  getInstance(_id: string): Observable<InteractiveDocumentInstance> {
    return this.httpClient.get<InteractiveDocumentInstance>(this.API_URL + _id);
  }


  // Value History: record-field changes this document instance has pushed via field mappings.
  getValueChanges(_id: string): Observable<any[]> {
    return this.httpClient.get<any[]>(this.API_URL + _id + '/value-changes');
  }


  delete(id: string): Observable<any> {
    return this.httpClient.put(this.API_URL + 'mark-deleted/' + id, this.headerOptions);
  }


  deleteByIdArray(arrayOfIds): Observable<any[]> {
    const deleteRequest = { ids: arrayOfIds };
    return this.httpClient.put<any>(this.API_URL + 'mark-as-deleted/by-array', JSON.stringify(deleteRequest), this.headerOptions);
  }


  getDataSource(_searchTerms, sortField = 'name', sortDirection = 'asc', pageNumber: number = 0, pageSize: number = 10): Observable<InteractiveDocumentInstanceApi> {
    _searchTerms['selectAll'] = false;
    _searchTerms.sortField = sortField;
    _searchTerms.sortOrder = sortDirection;
    _searchTerms.pageNumber = pageNumber;
    _searchTerms.pageSize = pageSize;

    return this.httpClient.post<InteractiveDocumentInstanceApi>(this.API_URL + 'dataSourceSearch', JSON.stringify(_searchTerms), this.headerOptions);
  }
}